import type { AuthoredGridPose, CoasterProject } from '../schema/index.ts';
import { AUTHORED_TRACK_CATALOG, getAuthoredDescriptor } from './catalog.ts';
import { descriptorExitPose, posesConnect } from './fixed-grid.ts';
import {
  appendAuthoredPiece,
  authoredEndPose,
  authoredStartPose,
  closeAuthoredCircuit,
  createAuthoredCandidateEndingAt,
  prependAuthoredCandidate,
} from './operations.ts';
import type { AuthoredBuildResult } from './types.ts';

export interface AuthoredFinishPlan {
  readonly descriptorIds: readonly string[];
  readonly exploredStates: number;
  readonly project: CoasterProject;
  readonly mode: 'standard';
  readonly customTailSectionIds: readonly string[];
}

export interface AuthoredFinishLimits {
  readonly maxPieces?: number;
  readonly maxStates?: number;
  readonly timeBudgetMs?: number;
  readonly validateFirstStep?: (project: CoasterProject) => string | null;
  readonly validateProject?: (project: CoasterProject) => string | null;
}

const FINISH_DESCRIPTOR_IDS = new Set([
  'straight.0_5t',
  'straight.1t',
  'straight.2t',
  'straight.3t',
  'turn.left.45.small',
  'turn.right.45.small',
  'turn.left.90.small',
  'turn.right.90.small',
  'turn.left.45.medium',
  'turn.right.45.medium',
  'turn.left.90.medium',
  'turn.right.90.medium',
  'turn.left.90.large',
  'turn.right.90.large',
  'slope.0.25',
  'slope.25.60',
  'slope.60.90',
  'slope.90.60',
  'slope.60.25',
  'slope.25.0',
  'slope.0.n25',
  'slope.n25.n60',
  'slope.n60.n90',
  'slope.n90.n60',
  'slope.n60.n25',
  'slope.n25.0',
  'slope.25.hold.run0_5t',
  'slope.25.hold.run1t',
  'slope.25.hold.run2t',
  'slope.25.hold.run3t',
  'slope.n25.hold.run0_5t',
  'slope.n25.hold.run1t',
  'slope.n25.hold.run2t',
  'slope.n25.hold.run3t',
  'slope.60.hold.run0_5t',
  'slope.60.hold.run1t',
  'slope.60.hold.run2t',
  'slope.60.hold.run3t',
  'slope.n60.hold.run0_5t',
  'slope.n60.hold.run1t',
  'slope.n60.hold.run2t',
  'slope.n60.hold.run3t',
  'slope.90.hold.rise0_5t',
  'slope.90.hold.rise1t',
  'slope.90.hold.rise2t',
  'slope.90.hold.rise3t',
  'slope.n90.hold.rise0_5t',
  'slope.n90.hold.rise1t',
  'slope.n90.hold.rise2t',
  'slope.n90.hold.rise3t',
  'bank.level.left.1t',
  'bank.left.level.1t',
  'bank.level.right.1t',
  'bank.right.level.1t',
]);

const FINISH_DESCRIPTORS = AUTHORED_TRACK_CATALOG.filter((descriptor) =>
  FINISH_DESCRIPTOR_IDS.has(descriptor.id),
);

const ROUTE_TURN_PENALTY = 2;
const ROUTE_TRANSITION_PENALTY = 1;
const ALTERNATE_PATHS_PER_POSE = 3;
const ALTERNATE_ROUTE_REFINEMENT_STATES = 2_000;
const SIMPLICITY_REFINEMENT_STATES = 250;
const MAX_ROUTE_STEP_DISTANCE = Math.max(
  1,
  ...FINISH_DESCRIPTORS.map((descriptor) => {
    const end = descriptor.spans.at(-1)?.end ?? { x: 0, y: 0, z: 0 };
    return Math.hypot(end.x, end.y, end.z);
  }),
);

function poseKey(pose: AuthoredGridPose): string {
  return [
    pose.x,
    pose.y,
    pose.z,
    pose.headingStep,
    pose.pitchDegrees,
    pose.bank,
    pose.orientation,
  ].join('|');
}

function headingDistance(first: number, second: number): number {
  return Math.min(Math.abs(first - second), 8 - Math.abs(first - second));
}

function heuristic(pose: AuthoredGridPose, target: AuthoredGridPose): number {
  const spatial =
    (Math.abs(pose.x - target.x) + Math.abs(pose.y - target.y) + Math.abs(pose.z - target.z)) / 32;
  const headingDelta = headingDistance(pose.headingStep, target.headingStep);
  const pitchPenalty = pose.pitchDegrees === target.pitchDegrees ? 0 : 3;
  const bankPenalty = pose.bank === target.bank ? 0 : 2;
  const orientationPenalty = pose.orientation === target.orientation ? 0 : 1;
  return spatial + headingDelta + pitchPenalty + bankPenalty + orientationPenalty;
}

function pieceCountHeuristic(pose: AuthoredGridPose, target: AuthoredGridPose): number {
  const spatial =
    Math.hypot(pose.x - target.x, pose.y - target.y, pose.z - target.z) / MAX_ROUTE_STEP_DISTANCE;
  const turns = Math.ceil(headingDistance(pose.headingStep, target.headingStep) / 2);
  return Math.max(spatial, turns, minimumTransitionCount(pose, target));
}

function inBounds(project: CoasterProject, pose: AuthoredGridPose): boolean {
  const halfX = Math.floor((project.plot.sizeMeters.x / 2) * 8);
  const halfZ = Math.floor((project.plot.sizeMeters.z / 2) * 8);
  const ground = Math.round(project.plot.groundElevationMeters * 8);
  return (
    pose.x >= -halfX &&
    pose.x <= halfX &&
    pose.z >= -halfZ &&
    pose.z <= halfZ &&
    pose.y >= ground + 16 &&
    pose.y <= ground + 1_600
  );
}

interface SearchState {
  readonly pose: AuthoredGridPose;
  readonly path: readonly string[];
  readonly cost: number;
  readonly turnCount: number;
  readonly transitionCount: number;
  readonly score: number;
}

interface CompletedRoute {
  readonly state: SearchState;
  readonly project: CoasterProject;
}

function compareSearchStates(first: SearchState, second: SearchState): number {
  return (
    first.score - second.score ||
    first.turnCount - second.turnCount ||
    first.transitionCount - second.transitionCount ||
    first.path.length - second.path.length
  );
}

function compareCompletedRoutes(first: CompletedRoute, second: CompletedRoute): number {
  return (
    first.state.turnCount - second.state.turnCount ||
    first.state.transitionCount - second.state.transitionCount ||
    first.state.path.length - second.state.path.length ||
    first.state.cost - second.state.cost
  );
}

function minimumTransitionCount(first: AuthoredGridPose, second: AuthoredGridPose): number {
  return (
    Number(first.pitchDegrees !== second.pitchDegrees) +
    Number(first.bank !== second.bank) +
    Number(first.orientation !== second.orientation)
  );
}

function routeStepComplexity(
  descriptorId: string,
  entry: AuthoredGridPose,
  exit: AuthoredGridPose,
): { readonly cost: number; readonly turns: number; readonly transitions: number } {
  const descriptor = getAuthoredDescriptor(descriptorId);
  const turns = descriptor && descriptor.exit.headingDelta !== 0 ? 1 : 0;
  const transitions =
    Number(entry.pitchDegrees !== exit.pitchDegrees) +
    Number(entry.bank !== exit.bank) +
    Number(entry.orientation !== exit.orientation);
  return {
    cost: 1 + turns * ROUTE_TURN_PENALTY + transitions * ROUTE_TRANSITION_PENALTY,
    turns,
    transitions,
  };
}

function retainPoseAlternative(
  costsByPose: Map<string, number[]>,
  key: string,
  cost: number,
): boolean {
  const costs = costsByPose.get(key) ?? [];
  if (costs.length >= ALTERNATE_PATHS_PER_POSE && costs.at(-1)! <= cost) return false;
  costs.push(cost);
  costs.sort((first, second) => first - second);
  if (costs.length > ALTERNATE_PATHS_PER_POSE) costs.length = ALTERNATE_PATHS_PER_POSE;
  costsByPose.set(key, costs);
  return true;
}

function pushSearchState(queue: SearchState[], state: SearchState): void {
  queue.push(state);
  let index = queue.length - 1;
  while (index > 0) {
    const parentIndex = Math.floor((index - 1) / 2);
    const parent = queue[parentIndex]!;
    if (compareSearchStates(parent, state) <= 0) break;
    queue[index] = parent;
    index = parentIndex;
  }
  queue[index] = state;
}

function popSearchState(queue: SearchState[]): SearchState | undefined {
  const first = queue[0];
  const last = queue.pop();
  if (!first || !last || queue.length === 0) return first;
  let index = 0;
  while (true) {
    const leftIndex = index * 2 + 1;
    if (leftIndex >= queue.length) break;
    const rightIndex = leftIndex + 1;
    const childIndex =
      rightIndex < queue.length && compareSearchStates(queue[rightIndex]!, queue[leftIndex]!) < 0
        ? rightIndex
        : leftIndex;
    if (compareSearchStates(last, queue[childIndex]!) <= 0) break;
    queue[index] = queue[childIndex]!;
    index = childIndex;
  }
  queue[index] = last;
  return first;
}

function prioritizeShortestPaths(
  queue: SearchState[],
  target: AuthoredGridPose,
  maxTurns: number,
): void {
  const candidates = queue.splice(0).filter((state) => state.turnCount <= maxTurns);
  for (const state of candidates) {
    pushSearchState(queue, {
      ...state,
      score: state.path.length + pieceCountHeuristic(state.pose, target),
    });
  }
}

function appendPath(
  project: CoasterProject,
  descriptorIds: readonly string[],
): AuthoredBuildResult<CoasterProject> {
  let candidate = project;
  for (const descriptorId of descriptorIds) {
    const appended = appendAuthoredPiece(candidate, descriptorId);
    if (!appended.ok) return appended;
    candidate = appended.value;
  }
  return { ok: true, value: candidate };
}

function planAuthoredRoute(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This finish solver only supports authored-grid tracks.',
    };
  if (project.track.closed)
    return { ok: false, code: 'already_closed', message: 'This circuit is already closed.' };
  const component = project.track.authoredTopology.components[0];
  const pieceById = new Map(project.track.pieces.map((piece) => [piece.id, piece]));
  const firstPiece = component ? pieceById.get(component.pieceIds[0]!) : null;
  const nextComponent = project.track.authoredTopology.components.find(
    (candidate) => candidate.id !== component?.id,
  );
  const nextComponentHead = nextComponent ? pieceById.get(nextComponent.pieceIds[0]!) : null;
  const station = project.track.pieces.find(
    (piece) => getAuthoredDescriptor(piece.descriptorId)?.category === 'station',
  );
  const start = authoredEndPose(project);
  if (!station || !component || !firstPiece || !start)
    return { ok: false, code: 'station_required', message: 'Place a station first.' };
  if (project.track.pieces.length < 4)
    return {
      ok: false,
      code: 'needs_track',
      message: 'Build at least three pieces after the station first.',
    };
  const target = nextComponentHead?.entryPose ?? firstPiece.entryPose;
  const maxPieces = limits.maxPieces ?? 12;
  const maxStates = limits.maxStates ?? 25_000;
  const deadline = Date.now() + (limits.timeBudgetMs ?? 100);
  const queue: SearchState[] = [
    {
      pose: start,
      path: [],
      cost: 0,
      turnCount: 0,
      transitionCount: 0,
      score: heuristic(start, target),
    },
  ];
  const costsByPose = new Map<string, number[]>([[poseKey(start), [0]]]);
  let exploredStates = 0;
  let lastRejectedRoute: string | null = null;
  let bestCompletedRoute: CompletedRoute | null = null;
  let firstCompletedRouteFoundAt: number | null = null;
  let simplestRouteFoundAt: number | null = null;
  const minimumTurns = Math.ceil(headingDistance(start.headingStep, target.headingStep) / 2);
  const minimumTransitions = minimumTransitionCount(start, target);

  while (queue.length && exploredStates < maxStates && Date.now() <= deadline) {
    if (
      firstCompletedRouteFoundAt !== null &&
      simplestRouteFoundAt === null &&
      exploredStates - firstCompletedRouteFoundAt >= ALTERNATE_ROUTE_REFINEMENT_STATES
    )
      break;
    if (
      simplestRouteFoundAt !== null &&
      exploredStates - simplestRouteFoundAt >= SIMPLICITY_REFINEMENT_STATES
    )
      break;
    const current = popSearchState(queue)!;
    exploredStates += 1;
    if (posesConnect(current.pose, target)) {
      const appended = appendPath(project, current.path);
      if (!appended.ok) {
        if (appended.code === 'track_overlap') {
          lastRejectedRoute = appended.message;
          continue;
        }
        return appended;
      }
      const candidate = appended.value;
      const repairedComponentGap = project.track.authoredTopology.components.length > 1;
      const completed =
        candidate.track.closed || repairedComponentGap
          ? ({ ok: true, value: candidate } as const)
          : closeAuthoredCircuit(candidate);
      if (!completed.ok) return completed;
      const rejection = limits.validateProject?.(completed.value) ?? null;
      if (rejection) {
        lastRejectedRoute = rejection;
        continue;
      }
      const completedRoute = { state: current, project: completed.value };
      if (!bestCompletedRoute || compareCompletedRoutes(completedRoute, bestCompletedRoute) < 0)
        bestCompletedRoute = completedRoute;
      firstCompletedRouteFoundAt ??= exploredStates;
      if (current.turnCount === minimumTurns && current.transitionCount === minimumTransitions) {
        if (simplestRouteFoundAt === null) {
          simplestRouteFoundAt = exploredStates;
          prioritizeShortestPaths(queue, target, current.turnCount);
        }
      }
      continue;
    }
    if (current.path.length >= maxPieces) continue;
    for (const descriptor of FINISH_DESCRIPTORS) {
      if (!descriptor.entry.pitches.includes(current.pose.pitchDegrees)) continue;
      if (!descriptor.entry.banks.includes(current.pose.bank)) continue;
      if (!descriptor.entry.orientations.includes(current.pose.orientation)) continue;
      if (current.path.length === 0 && limits.validateFirstStep) {
        const firstStep = appendAuthoredPiece(project, descriptor.id);
        if (!firstStep.ok || limits.validateFirstStep(firstStep.value)) continue;
      }
      const nextPose = descriptorExitPose(current.pose, descriptor);
      if (!inBounds(project, nextPose)) continue;
      const nextPath = [...current.path, descriptor.id];
      const step = routeStepComplexity(descriptor.id, current.pose, nextPose);
      if (bestCompletedRoute && current.turnCount + step.turns > bestCompletedRoute.state.turnCount)
        continue;
      const nextCost = current.cost + step.cost;
      const key = poseKey(nextPose);
      if (!posesConnect(nextPose, target) && !retainPoseAlternative(costsByPose, key, nextCost))
        continue;
      pushSearchState(queue, {
        pose: nextPose,
        path: nextPath,
        cost: nextCost,
        turnCount: current.turnCount + step.turns,
        transitionCount: current.transitionCount + step.transitions,
        score:
          simplestRouteFoundAt === null
            ? nextCost + heuristic(nextPose, target)
            : nextPath.length + pieceCountHeuristic(nextPose, target),
      });
    }
  }
  if (bestCompletedRoute) {
    return {
      ok: true,
      value: {
        descriptorIds: bestCompletedRoute.state.path,
        exploredStates,
        project: bestCompletedRoute.project,
        mode: 'standard',
        customTailSectionIds: [],
      },
    };
  }
  return {
    ok: false,
    code: Date.now() > deadline ? 'timeout' : 'no_standard_route',
    message: lastRejectedRoute
      ? 'I found routes that line up with the station, but the routes checked cross existing track. Add a short straight or turn to create more space, then try again.'
      : Date.now() > deadline
        ? "I couldn't finish searching from this position yet. Add a short straight or turn, or use Route Guide, then try again."
        : "I couldn't find a clear route from this position. Add a short straight or turn, or use Route Guide, then try again.",
  };
}

export function planAuthoredFinish(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  return planAuthoredRoute(project, limits);
}

export function planAuthoredAutocomplete(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  return planAuthoredRoute(project, limits);
}

export function planAuthoredReverseAutocomplete(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This finish solver only supports authored-grid tracks.',
    };
  const component = project.track.authoredTopology.components[0];
  const pieceById = new Map(project.track.pieces.map((piece) => [piece.id, piece]));
  const targetComponent =
    project.track.authoredTopology.components.find((candidate) => candidate.id !== component?.id) ??
    component;
  const targetTail = targetComponent ? pieceById.get(targetComponent.pieceIds.at(-1)!) : null;
  const start = authoredStartPose(project, component?.id);
  const target = targetTail?.exitPose;
  if (!component || !start || !target)
    return { ok: false, code: 'needs_track', message: 'Select a repair endpoint first.' };
  const maxPieces = limits.maxPieces ?? 12;
  const maxStates = limits.maxStates ?? 25_000;
  const deadline = Date.now() + (limits.timeBudgetMs ?? 100);
  const queue: SearchState[] = [
    {
      pose: start,
      path: [],
      cost: 0,
      turnCount: 0,
      transitionCount: 0,
      score: heuristic(start, target),
    },
  ];
  const costsByPose = new Map<string, number[]>([[poseKey(start), [0]]]);
  let exploredStates = 0;
  let lastRejectedRoute: string | null = null;
  let bestCompletedRoute: CompletedRoute | null = null;
  let firstCompletedRouteFoundAt: number | null = null;
  let simplestRouteFoundAt: number | null = null;
  const minimumTurns = Math.ceil(headingDistance(start.headingStep, target.headingStep) / 2);
  const minimumTransitions = minimumTransitionCount(start, target);

  const applyPath = (descriptorIds: readonly string[]) => {
    let candidateProject = project;
    for (const descriptorId of descriptorIds) {
      if (candidateProject.track.geometryKind !== 'authored_grid_v2') break;
      const activeComponent = candidateProject.track.authoredTopology.components.find(
        (candidate) => candidate.id === component.id,
      );
      const head = authoredStartPose(candidateProject, activeComponent?.id);
      if (!head)
        return { ok: false as const, code: 'missing_endpoint', message: 'The endpoint moved.' };
      const candidate = createAuthoredCandidateEndingAt(descriptorId, head);
      if (!candidate.ok) return candidate;
      const prepended = prependAuthoredCandidate(
        candidateProject,
        candidate.value,
        activeComponent?.id,
      );
      if (!prepended.ok) return prepended;
      candidateProject = prepended.value;
    }
    return { ok: true as const, value: candidateProject };
  };

  while (queue.length && exploredStates < maxStates && Date.now() <= deadline) {
    if (
      firstCompletedRouteFoundAt !== null &&
      simplestRouteFoundAt === null &&
      exploredStates - firstCompletedRouteFoundAt >= ALTERNATE_ROUTE_REFINEMENT_STATES
    )
      break;
    if (
      simplestRouteFoundAt !== null &&
      exploredStates - simplestRouteFoundAt >= SIMPLICITY_REFINEMENT_STATES
    )
      break;
    const current = popSearchState(queue)!;
    exploredStates += 1;
    if (posesConnect(target, current.pose)) {
      const repaired = applyPath(current.path);
      if (!repaired.ok) {
        lastRejectedRoute = repaired.message;
        continue;
      }
      const rejection = limits.validateProject?.(repaired.value) ?? null;
      if (rejection) {
        lastRejectedRoute = rejection;
        continue;
      }
      const completedRoute = { state: current, project: repaired.value };
      if (!bestCompletedRoute || compareCompletedRoutes(completedRoute, bestCompletedRoute) < 0)
        bestCompletedRoute = completedRoute;
      firstCompletedRouteFoundAt ??= exploredStates;
      if (current.turnCount === minimumTurns && current.transitionCount === minimumTransitions) {
        if (simplestRouteFoundAt === null) {
          simplestRouteFoundAt = exploredStates;
          prioritizeShortestPaths(queue, target, current.turnCount);
        }
      }
      continue;
    }
    if (current.path.length >= maxPieces) continue;
    for (const descriptor of FINISH_DESCRIPTORS) {
      const candidate = createAuthoredCandidateEndingAt(descriptor.id, current.pose);
      if (!candidate.ok || !inBounds(project, candidate.value.entryPose)) continue;
      const nextPath = [...current.path, descriptor.id];
      const step = routeStepComplexity(descriptor.id, candidate.value.entryPose, current.pose);
      if (bestCompletedRoute && current.turnCount + step.turns > bestCompletedRoute.state.turnCount)
        continue;
      const nextCost = current.cost + step.cost;
      const key = poseKey(candidate.value.entryPose);
      if (
        !posesConnect(target, candidate.value.entryPose) &&
        !retainPoseAlternative(costsByPose, key, nextCost)
      )
        continue;
      pushSearchState(queue, {
        pose: candidate.value.entryPose,
        path: nextPath,
        cost: nextCost,
        turnCount: current.turnCount + step.turns,
        transitionCount: current.transitionCount + step.transitions,
        score:
          simplestRouteFoundAt === null
            ? nextCost + heuristic(candidate.value.entryPose, target)
            : nextPath.length + pieceCountHeuristic(candidate.value.entryPose, target),
      });
    }
  }
  if (bestCompletedRoute) {
    return {
      ok: true,
      value: {
        descriptorIds: bestCompletedRoute.state.path,
        exploredStates,
        project: bestCompletedRoute.project,
        mode: 'standard',
        customTailSectionIds: [],
      },
    };
  }
  return {
    ok: false,
    code: Date.now() > deadline ? 'timeout' : 'no_standard_route',
    message: lastRejectedRoute
      ? 'I found routes that line up with the opposite endpoint, but the routes checked cross existing track. Add a short straight or turn, then try again.'
      : "I couldn't find a clear reverse route from this position. Switch sides or continue manually.",
  };
}
